import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiRequests } from '../../shared/api/apiRequests';
import { Review } from '../../entities/Review';
import { Pagination } from '../../features/Pagination/index.js';
import { useUrlPagination } from '../../shared/hooks/useUrlPagination.js';
import { toast, ToastContainer } from 'react-toastify';
import { PageBar } from '../../shared/ui/PageBar';
import { EmptyState } from '../../shared/ui/EmptyState';
import { MessageSquareQuote, ShieldCheck } from 'lucide-react';

const ReviewsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { page, offset, pageSize, setPage } = useUrlPagination(10);
  const isModeration = searchParams.get('type') === 'moderation';
  // Применённый поиск — в URL, в поле — черновик
  const query = searchParams.get('q') || '';
  const [reviews, setReviews] = useState({});
  const [searchValue, setSearchValue] = useState(query);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => setSearchValue(query), [query]);

  useEffect(() => {
    let cancelled = false;
    apiRequests.reviews
      .getWithFilters(query || undefined, isModeration ? true : undefined, pageSize, offset)
      .then((res) => {
        if (!cancelled) setReviews(res);
      });
    return () => {
      cancelled = true;
    };
  }, [page, query, isModeration, reloadKey]);

  // Изменить параметры URL и вернуться на первую страницу
  const applyParams = (values) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      Object.entries(values).forEach(([key, value]) => {
        if (value) params.set(key, value);
        else params.delete(key);
      });
      params.delete('page');
      return params;
    });
  };

  const handleDelete = async (id) => {
    const request = apiRequests.reviews.delete(id).then(() => {
      if (reviews.items?.length === 1 && page > 1) setPage(page - 1);
      else setReloadKey((k) => k + 1);
    });
    toast.promise(request, {
      pending: 'Идёт запрос...',
      error: 'Произошла ошибка',
      success: 'Отзыв успешно удалён',
    });
  };

  const handleVisible = async (id, value) => {
    await apiRequests.reviews.hide(id, value).then(() =>
      setReviews((prev) => {
        const arr = prev.items.map((i) => {
          if (i.id !== id) {
            return i;
          } else {
            return {
              ...i,
              isHidden: value,
            };
          }
        });
        return { totalCount: prev.totalCount, items: arr };
      }),
    );
  };

  const handleSearch = () => applyParams({ q: searchValue.trim() });

  return (
    <>
      <PageBar
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        onSearch={handleSearch}
        searchPlaceholder="Поиск отзывов..."
      />

      <nav className="nav at-nav nav-pills flex-column flex-sm-row mb-3">
        <button
          onClick={() => applyParams({ type: 'moderation' })}
          className={`flex-sm-fill text-sm-center nav-link ${isModeration && 'active'}`}
        >
          Ожидают модерации
        </button>
        <button
          onClick={() => applyParams({ type: null })}
          className={`flex-sm-fill text-sm-center nav-link ${!isModeration && 'active'}`}
          aria-current="page"
        >
          Все отзывы
        </button>
      </nav>

      <div className="page-table mb-3">
        {reviews.items?.length === 0 ? (
          query ? (
            <EmptyState
              icon={MessageSquareQuote}
              variant="search"
              title="Отзывы не найдены"
              description="Ни в одном отзыве нет такого текста. Попробуйте другое слово или имя автора."
            />
          ) : isModeration ? (
            <EmptyState
              icon={ShieldCheck}
              title="Очередь модерации пуста"
              description="Все новые отзывы уже проверены. Когда клиенты оставят свежие, они появятся на этой вкладке."
            />
          ) : (
            <EmptyState
              icon={MessageSquareQuote}
              title="Отзывов пока нет"
              description="Отзывы оставляют клиенты на сайте — здесь их можно будет скрыть, вернуть в публикацию или удалить."
            />
          )
        ) : (
          reviews.items?.map((i) => (
            <Review onChangeVisible={handleVisible} onDelete={handleDelete} key={i.id} data={i} />
          ))
        )}
      </div>

      <Pagination
        page={page}
        total={reviews.totalCount}
        pageSize={pageSize}
        onPageChange={setPage}
      />
      <ToastContainer />
    </>
  );
};

export default ReviewsPage;
